import React, { useCallback, useEffect, useRef, useState } from 'react';
import cx from 'classnames';
import { Form } from 'react-bootstrap';

import TruncatedTextWithTooltip from 'components/TruncatedTextWithTooltip';

import styles from './personalization.module.scss';

const EMPTY_NAME_ERROR = 'Please provide a valid name';

/**
 * Title + rename field — matches Audience Segment (campaign settings) collapsable block.
 */
export const PersonalizationSetNameField = ({
  set,
  isExpanded,
  onExpand,
  onRename,
  renameMode = false,
  onRenameModeChange,
}) => {
  const [rename, setRename] = useState(false);
  const [name, setName] = useState(set.name || '');
  const [errorMessage, setErrorMessage] = useState(null);
  const inputRef = useRef(null);
  const skipBlurRef = useRef(false);

  const isEditing = rename || renameMode;

  useEffect(() => {
    if (!isEditing) {
      setName(set.name || '');
    }
  }, [set.name, isEditing]);

  const beginRename = useCallback(() => {
    skipBlurRef.current = true;
    setRename(true);
    setName(set.name || '');
    setErrorMessage(null);
    onRenameModeChange?.(true);
    window.setTimeout(() => {
      skipBlurRef.current = false;
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
  }, [onRenameModeChange, set.name]);

  useEffect(() => {
    if (!renameMode) {
      return undefined;
    }
    skipBlurRef.current = true;
    setRename(true);
    setName(set.name || '');
    setErrorMessage(null);
    const timer = window.setTimeout(() => {
      skipBlurRef.current = false;
      inputRef.current?.focus();
      inputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [renameMode]);

  const handleSaveChanges = useCallback(() => {
    const next = name.trim();
    if (!next) {
      setErrorMessage(EMPTY_NAME_ERROR);
      inputRef.current?.focus();
      return;
    }
    setErrorMessage(null);
    setRename(false);
    onRenameModeChange?.(false);
    onRename(next);
  }, [name, onRename, onRenameModeChange]);

  const cancelRename = useCallback(() => {
    setRename(false);
    setErrorMessage(null);
    setName(set.name || '');
    onRenameModeChange?.(false);
  }, [onRenameModeChange, set.name]);

  const startRename = useCallback(
    (event) => {
      event.stopPropagation();
      if (!isExpanded) {
        onExpand();
      }
      beginRename();
    },
    [isExpanded, onExpand, beginRename],
  );

  return (
    <div
      className={styles.setCollapsable}
      onClick={(e) => {
        if (!isExpanded && !isEditing) {
          e.stopPropagation();
          onExpand();
        }
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !isExpanded && !isEditing) {
          onExpand();
        }
      }}
      role={!isExpanded && !isEditing ? 'button' : undefined}
      tabIndex={!isExpanded && !isEditing ? 0 : undefined}
    >
      {isEditing ? (
        <Form.Group className={styles.siteNameFormGroup}>
          <Form.Control
            type="text"
            ref={inputRef}
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              setErrorMessage(null);
            }}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSaveChanges();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                cancelRename();
              }
            }}
            onBlur={() => {
              if (skipBlurRef.current) {
                return;
              }
              handleSaveChanges();
            }}
            isInvalid={!!errorMessage}
            aria-label="Set name"
          />
          <Form.Control.Feedback type="invalid" className={styles.nameFieldError}>
            {errorMessage}
          </Form.Control.Feedback>
        </Form.Group>
      ) : (
        <div className={styles.segmentTitle} onDoubleClick={startRename}>
          <TruncatedTextWithTooltip content={name} maxWidth={140} placement="top" />
        </div>
      )}
    </div>
  );
};
